import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { OverlayNotificationsService } from '../../services/overlay-notifications.service';
import { Notification } from '../../models/overlay-notifications.model';
import { UsersService } from '../../../features/users/services/users.service';
import { ToastService } from 'declarabid-library';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'app-overlay-notifications',
  imports: [CommonModule, SkeletonModule],
  templateUrl: './overlay-notifications.component.html',
  styleUrl: './overlay-notifications.component.scss',
})
export class OverlayNotificationsComponent {
  private readonly notificationService = inject(OverlayNotificationsService);
  private readonly _user = inject(UsersService).currentUser();
  private readonly toastService = inject(ToastService);

  protected readonly loading = this.notificationService.loading;

  protected notifications = signal<Notification[]>([]);

  private readonly ROLE_MAP: Record<string, string> = {
    administrator: $localize`Administrador`,
    rrhh: $localize`Recursos Humanos`,
    declarant: $localize`Declarante`,
    reviewer: $localize`Revisor`,
    system: $localize`Sistema`,
  };

  protected readonly unreadCountLabel = computed(() =>
    this.notificationService.hasUnreadCount() > 0
      ? `(${this.notificationService.hasUnreadCount()})`
      : '',
  );
  protected readonly hasUnreadCount = this.notificationService.hasUnreadCount;

  private readonly _ = effect(
    () => {
      const data = this.notificationService.notificationsData();
      if (data) {
        this.notifications.set([...data.notifications].reverse());
      }
    },
    { allowSignalWrites: true },
  );

  protected getIcon(type: string): string {
    switch (type) {
      case 'message':
        return 'pi-comment';
      case 'event ':
        return 'pi-calendar';
      case 'reminder':
        return 'pi-bell';
      default:
        return 'pi-comment';
    }
  }

  protected getTitle(type: string): string {
    switch (type) {
      case 'message':
        return $localize`Mensaje nuevo:`;
      case 'event ':
        return $localize`Evento:`;
      case 'reminder':
        return $localize`Recordatorio:`;
      default:
        return $localize`Notificación:`;
    }
  }

  protected getMessage(n: Notification): string {
    const sender = this.getSender(n);
    const template = n.email_template?.email_template_type ?? '';

    if (template === 'email_template_declarations_to_finish') {
      return n.notification_is_automatic
        ? $localize`Recuerda que su declaración está por vencer:`
        : sender +
            ' ' +
            $localize`le recuerda que la declaración está por vencer:`;
    }

    if (template === 'email_template_copy_of_correct_answers') {
      return $localize`Se realizo correctamente la copia de respuestas`;
    }

    if (template === 'email_template_copy_failed') {
      return $localize`Ha ocurrido una falla en el proceso de copia de respuestas en una de sus asignaciones`;
    }

    if (template === 'email_template_copy_of_correct_answers_declarant') {
      return $localize`Se realizo correctamente la copia de respuestas`;
    }

    return (
      this.MESSAGE_MAP[template]?.(sender) ??
      sender + ' ' + $localize`le ha enviado una nueva notificación:`
    );
  }

  private readonly MESSAGE_MAP: Record<string, (sender: string) => string> = {
    email_template_reviewer_or_admin_to_declarant: (sender) =>
      sender + ' ' + $localize`le ha dejado un mensaje en la declaración:`,

    email_template_declarant_response: (sender) =>
      sender + ' ' + $localize`le ha dejado un mensaje en la declaración:`,

    email_template_send_declaration: (sender) =>
      sender + ' ' + $localize`se ha enviado la declaración:`,

    email_template_assign_to_declarant: (sender) =>
      sender + ' ' + $localize`le ha asignado la declaración:`,

    email_template_assign_to_reviewer: (sender) =>
      sender +
      ' ' +
      $localize`le ha seleccionado como revisor en una asignación`,

    email_template_declaration_expiration: (sender) =>
      sender +
      ' ' +
      $localize`le ha notificado que acaba de vencer su declaración:`,

    email_template_declarant_submission: () =>
      $localize`Se le notifica que se ha enviado la declaración:`,

    email_template_review_finished: (sender) =>
      sender +
      ' ' +
      $localize`le ha notificado que ha finalizado la revisión de su declaración:`,

    email_template_escalated_to_admin: (sender) =>
      sender + ' ' + $localize`le ha transferido la declaración:`,

    email_template_time_extension: (sender) =>
      sender +
      ' ' +
      $localize`le ha aprobado su solicitud de extensión de envió de la declaración:`,

    email_template_extension_by_declarant: (sender) =>
      sender +
      ' ' +
      $localize`le ha solicitado una extensión de tiempo de la declaración:`,

    email_template_administrative_closure: (sender) =>
      sender + ' ' + $localize`le ha solicitado un cierre administrativo:`,

    email_template_extension_returned_the_declarant: (sender) =>
      sender + ' ' + $localize`le ha devuelto la declaración:`,

    email_template_return_to_declarant: (sender) =>
      sender + ' ' + $localize`le ha devuelto la declaración:`,

    email_template_revised_form: (sender) =>
      sender + ' ' + $localize`ha finalizado la revisión de la declaración:`,

    email_template_declarant_resend: (sender) =>
      sender + ' ' + $localize`ha reenviado una declaración:`,
  };

  private getSender(n: Notification): string {
    const name = n.notification_sender_name?.trim();
    const roleKey = n.notification_sender_role_value?.trim();
    const role = roleKey ? (this.ROLE_MAP[roleKey] ?? roleKey) : null;

    if (name && role) return `${name} (${role})`;
    if (name) return name;
    if (role) return role;

    return $localize`Usuario:`;
  }

  protected formatDate(date: string): string {
    return formatDate(date, 'd MMM yyyy', 'es-CO');
  }

  protected markAsRead(notification: Notification): void {
    if (!notification.notification_is_read) {
      this.notificationService
        .markNotificationAsRead(notification.notification_id)
        .subscribe({
          next: (res) => {
            if (!res.data.notification_is_read) {
              this.showError();
              return;
            }

            this.notifications.update((notifications) =>
              notifications.map((n) =>
                n.notification_id === notification.notification_id
                  ? { ...n, notification_is_read: true }
                  : n,
              ),
            );
            this.syncUnreadCount();
          },
          error: () => this.showError(),
        });
    }
  }

  private showError(): void {
    this.toastService.showError(
      $localize`Error`,
      $localize`Error al marcar como leída`,
    );
  }

  protected markAllAsRead(): void {
    const userId = this._user.id;
    this.notificationService.markAllNotificationsAsRead(userId).subscribe({
      next: (res) => {
        this.notifications.set([...res.data].reverse());
        this.syncUnreadCount();

        const hasUnread = res.data.some((n) => !n.notification_is_read);
        if (hasUnread) this.showErrorMarkAll();
      },
      error: () => this.showErrorMarkAll(),
    });
  }

  private showErrorMarkAll(): void {
    this.toastService.showError(
      $localize`Error`,
      $localize`Error al marcar todas como leídas`,
    );
  }

  protected deleteNotification(notificationId: number): void {
    this.notificationService.deleteNotification(notificationId).subscribe({
      next: () => {
        this.notificationService.forceRefresh(this._user.id);
      },
      error: () => {
        this.toastService.showError(
          $localize`Error`,
          $localize`No se pudo eliminar la notificación`,
        );
      },
    });
  }

  private syncUnreadCount(): void {
    const unreadCount = this.notifications().filter(
      (n) => !n.notification_is_read,
    ).length;
    this.notificationService.updateHasUnread(unreadCount);
  }
}
